import { liveReload } from '@shared/services/realtime.service';
import { PageLoaderComponent, PageLoad } from '@app/shared/components/page-loader/page-loader.component';
import { FilterChipsComponent, FilterChip } from '@app/shared/components/filter-bar/filter-chips.component';
import { SearchPillComponent } from '@app/shared/components/filter-bar/search-pill.component';
import { PageTitleComponent } from '@app/shared/components/page-title/page-title.component';
import { Component, OnInit, inject, signal, ViewChild } from '@angular/core';
import { ModulesService } from '@shared/services/modules.service';

import { Router } from '@angular/router';
// Material
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
// Services
import { UsersService } from '@shared/services/entities/users.service';
import { RolesService } from '@shared/services/entities/roles.service';
import { UserCustomParamsDialogComponent } from './user-custom-params-dialog/user-custom-params-dialog.component';
import { UserFormDialogComponent } from './user-form-dialog/user-form-dialog.component';
// Models
import { User, CreateUserPayload, UpdateUserPayload } from '@shared/models/entities/users.model';
import { RoleModel } from '@shared/models/entities/roles.model';
// Components / Services UI
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';
//Utils
import { sortAlphabetically } from '@shared/utils/sort.utils';
import { isPhoneUsername } from '@shared/security/phone-alias';

import { ListSelection, ListView } from '@app/shared/utils/list-view';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SelectionBarComponent } from '@app/shared/components/selection-bar/selection-bar.component';
import { SimpleExcelService } from '@shared/services/simple-excel.service';
@Component({
  selector: 'app-users',
  standalone: true,
  imports: [ MatButtonToggleModule, MatCheckboxModule, SelectionBarComponent,PageLoaderComponent, SearchPillComponent, FilterChipsComponent, PageTitleComponent, 
    MatTableModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatChipsModule,
    MatDialogModule
],
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss',
})
export class UsersComponent implements OnInit {
  /** Vista tabla / tarjetas y selección con checkbox (misma que en catálogos). */
  readonly listView = new ListView('users', 'table');
  readonly sel = new ListSelection<any>(() => (this.dataSource?.filteredData ?? []) as any[]);
  private readonly excelSvc = inject(SimpleExcelService);

  /** Exporta a Excel la selección. */
  async exportExcel(rows: any[]): Promise<void> {
    if (!rows?.length) return;
    await this.excelSvc.simpleXLSX({
      fileName: 'Usuarios',
      sheets: [{
        sheetName: 'Usuarios'.slice(0, 31), header: [], lineBreak: 0,
        columns: [
          { columnKey: 'name', columnName: 'Nombre' },
          { columnKey: '_contact', columnName: 'Correo' },
          { columnKey: '_role', columnName: 'Rol' },
        ],
        data: rows.map((r: any) => ({ ...r, _contact: this.contactOf(r), _role: this.getRoleName(r) })),
      }],
    } as any);
  }

  private modulesSvcTitle = inject(ModulesService);
  /** Título del módulo = etiqueta visible del catálogo de Módulos. */
  title = this.modulesSvcTitle.moduleTitle('/users', 'Usuarios');


  private readonly usersService         = inject(UsersService);
  private readonly rolesService         = inject(RolesService);
  private readonly customMessageService = inject(CustomMessageService);
  private readonly router               = inject(Router);
  private readonly dialog               = inject(MatDialog);

  // ─── Estado ────────────────────────────────────────────────────────────────
  loading = this.usersService.loading;

  // ─── Catálogos ─────────────────────────────────────────────────────────────
  availableRoles: RoleModel[] = [];

  // ─── Tabla ─────────────────────────────────────────────────────────────────
  columns    = ['select', 'avatar', 'name', 'email', 'role', 'actions'];
  dataSource = new MatTableDataSource<User>([]);
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  /** Pantalla de carga del módulo: bloquea hasta terminar la carga inicial. */
  readonly pageLoad = new PageLoad();
  /** Tiempo real: recarga cuando otro usuario cambia estos módulos (WebSocket). */
  private readonly live = liveReload(['users', 'customParams', 'roles'], () => this.pageLoad.ready && this.pageLoad.refresh(this.loadAll()));

  ngOnInit(): void {
    void this.init();
  }

  /** Carga inicial (también la usa "Reintentar"). */
  init(): Promise<boolean> {
    return this.pageLoad.run(() => this.initialLoad());
  }

  private async initialLoad(): Promise<void> {
    await this.loadAll();
  }

  /** true si el usuario entra con teléfono (no tiene correo real). */
  isPhoneUser(user: User): boolean {
    return !user.email || isPhoneUsername(user.email);
  }

  /** Correo real o teléfono, para mostrar en tabla y mensajes. */
  contactOf(user: User): string {
    return this.isPhoneUser(user) ? (user.phone ?? '') : user.email;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Carga de datos
  // ─────────────────────────────────────────────────────────────────────────────

  async loadAll(): Promise<void> {
    await Promise.all([this.loadUsers(), this.loadRoles()]);
  }

  async loadUsers(): Promise<void> {
    try {
      await this.usersService.search({ searchType: 'default', condition: { deleted: false } });
      this.dataSource.data = sortAlphabetically(this.usersService.listData(), 'name');
      this.dataSource.paginator = this.paginator;
    } catch {
      await this.customMessageService.open({
        title: 'Error', type: 'error',
        message: 'No se pudieron cargar los usuarios.', dismissBtn: true,
      });
    }
  }

  async loadRoles(): Promise<void> {
    try {
      await this.rolesService.search({ searchType: 'default', condition: { deleted: false } });
      this.availableRoles = sortAlphabetically(this.rolesService.listData(), 'name');
    } catch {
      console.error('No se pudieron cargar los roles');
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Alta / edición: en diálogo compartido (FormDialogShell) — título y
  // acciones fijos, contenido scrolleable — en vez del panel inline.
  // ─────────────────────────────────────────────────────────────────────────────

  openCreate(): void {
    this.openFormDialog();
  }

  openEdit(user: User): void {
    this.openFormDialog(user);
  }

  private openFormDialog(user?: User): void {
    const ref = this.dialog.open(UserFormDialogComponent, {
      data: { user, roles: this.availableRoles },
      width: '680px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      autoFocus: 'first-tabbable',
    });
    ref.afterClosed().subscribe(saved => {
      if (saved) this.loadUsers();
    });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // CRUD
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Abre el diálogo de "Sitios y especialidades" del usuario (por row).
   * Guarda en la tabla `customparams`: un registro único por usuario con
   * sus arreglos de sitios y especialidades.
   */
  openCustomParams(user: User): void {
    this.dialog.open(UserCustomParamsDialogComponent, {
      data: { user },
      width: '520px',
      maxHeight: '90vh',
    });
  }

  async resetPassword(user: User): Promise<void> {
    const esTecnico = this.isPhoneUser(user);
    const contacto = this.contactOf(user);
    const medio = esTecnico ? 'un SMS' : 'un correo';

    const confirm = await this.customMessageService.open({
      title: 'Reiniciar contraseña',
      type: 'warning',
      message:
        `¿Restablecer la contraseña de <b>${user.name}</b> ` +
        `(${contacto})?<br><br>` +
        `<small>El usuario recibirá ${medio} con una <b>nueva contraseña temporal</b>. ` +
        `Al iniciar sesión deberá establecer una contraseña nueva.</small>`,
      acceptBtn: true, dismissBtn: true,
    });
    if (!confirm) return;

    try {
      const result = await this.usersService.resetPassword({
        id:    user.id,
        email: esTecnico ? undefined : user.email,
        phone: esTecnico ? user.phone ?? undefined : undefined,
        name:  user.name,
      });

      const message = result?.action === 'reset_password'
        ? `Se envió ${medio} a <b>${contacto}</b> con un <b>código para reiniciar</b> su contraseña.`
        : `Se envió ${medio} a <b>${contacto}</b> con una <b>nueva contraseña temporal</b>. Al iniciar sesión deberá cambiarla.`;

      await this.customMessageService.open({
        title: '¡Listo!', type: 'success',
        message, duration: 3500,
      });
    } catch (err) {
      const message = err instanceof Error
        ? err.message
        : 'No se pudo reiniciar la contraseña.';
      await this.customMessageService.open({
        title: 'Error', type: 'error', message, dismissBtn: true,
      });
    }
  }

  async deleteUser(user: User): Promise<void> {
    const confirm = await this.customMessageService.open({
      title: 'Cuidado', type: 'warning',
      message: `¿Seguro que quieres eliminar al usuario <b>${user.name}</b>?`,
      acceptBtn: true, dismissBtn: true,
    });
    if (!confirm) return;

    try {
      await this.usersService.softDeleteUser(user.id);
      await this.loadUsers();
    } catch {
      await this.customMessageService.open({
        title: 'Error', type: 'error',
        message: 'No se pudo eliminar el usuario.', dismissBtn: true,
      });
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Búsqueda
  // ─────────────────────────────────────────────────────────────────────────────

  onSearch(event: Event): void {
    const val = (event.target as HTMLInputElement).value;
    this.dataSource.filter = val.trim().toLowerCase();
    this.dataSource.paginator?.firstPage();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Helpers template
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Devuelve el nombre del rol del usuario, o '—' si no tiene.
   */
  getRoleName(user: User): string {
    if (!user.role) return '—';
    return this.availableRoles.find(r => r.id === user.role)?.name ?? '—';
  }

  /** Iniciales del nombre para el avatar */
  getInitials(name?: string): string {
    if (!name) return '?';
    return name.trim().split(' ')
      .slice(0, 2)
      .map(w => w[0])
      .join('');
  }

  navigateTo(route: string): void {
    this.router.navigate([route]);
  }
}